export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// 兼容旧版本：首次加载后只在内存中保留遗留令牌，并立即清除持久化副本。
let accessToken: string | null = localStorage.getItem('crm_access_token');
let refreshToken: string | null = localStorage.getItem('crm_refresh_token');
localStorage.removeItem('crm_access_token');
localStorage.removeItem('crm_refresh_token');

/** 将服务端返回的令牌仅保存在当前页面内存中，浏览器持久认证由 HttpOnly Cookie 承担。 */
export function setTokens(access: string, refresh?: string): void {
  accessToken = access;
  refreshToken = refresh || null;
  localStorage.removeItem('crm_access_token');
  localStorage.removeItem('crm_refresh_token');
}

/** 清除内存令牌、遗留持久化令牌和缓存用户。 */
export function clearTokens(): void {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('crm_access_token');
  localStorage.removeItem('crm_refresh_token');
  localStorage.removeItem('crm_user');
}

export function getRefreshToken(): string | null {
  return refreshToken;
}

/** 根据内存令牌或缓存用户判断是否存在可恢复的浏览器会话。 */
export function isLoggedIn(): boolean {
  return !!accessToken || !!localStorage.getItem('crm_user');
}

export function saveUser(user: unknown): void {
  localStorage.setItem('crm_user', JSON.stringify(user));
}

export function loadUser<T>(): T | null {
  const data = localStorage.getItem('crm_user');
  if (!data) return null;
  try { return JSON.parse(data) as T; } catch { return null; }
}

export interface RequestConfig {
  onError?: (message: string, kind: 'error' | 'warning' | 'info') => void;
  onAuthExpired?: () => void;
}

let config: RequestConfig = {};
let refreshPromise: Promise<boolean> | null = null;

export function configureApi(c: RequestConfig): void {
  config = c;
}

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
}

/** 使用 HttpOnly Cookie 或遗留内存 refresh token 刷新会话，且并发请求只执行一次刷新。 */
async function refreshSession(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: refreshToken ? JSON.stringify({ refresh_token: refreshToken }) : undefined,
        credentials: 'include',
      });
      if (!response.ok) return false;
      const tokens = await response.json() as RefreshResponse;
      setTokens(tokens.access_token, tokens.refresh_token);
      return true;
    } catch {
      return false;
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/** 统一结束失效会话并通知宿主应用跳转登录页。 */
function expireSession(): void {
  clearTokens();
  config.onError?.('登录已过期，请重新登录', 'warning');
  config.onAuthExpired?.();
}

async function request<T>(
  method: string,
  endpoint: string,
  body?: unknown,
  timeoutMs: number = 30000,
  retryAuth = true,
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
      credentials: 'include',
    });
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'AbortError') {
      config.onError?.(`请求超时（${timeoutMs / 1000}s）`, 'error');
      throw new Error(`请求超时（${timeoutMs / 1000}s）`);
    }
    config.onError?.('请求失败，请检查网络连接', 'error');
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (response.status === 401) {
    const isAuthEndpoint = endpoint.startsWith('/auth/login') || endpoint.startsWith('/auth/refresh');
    if (!isAuthEndpoint && retryAuth && await refreshSession()) {
      return request<T>(method, endpoint, body, timeoutMs, false);
    }
    const error = await response.json().catch(() => ({ detail: '认证失败' }));
    if (!isAuthEndpoint) expireSession();
    throw new Error(error.detail || '认证失败');
  }

  if (response.status === 403) {
    const error = await response.json().catch(() => ({ detail: '无权限访问' }));
    throw new Error(`权限错误: ${error.detail || '您没有权限执行此操作'}`);
  }

  if (response.status === 422) {
    const error = await response.json().catch(() => ({ detail: '请求参数错误' }));
    const messages: string[] = [];
    if (Array.isArray(error.detail)) {
      for (const e of error.detail) {
        const field = e.loc?.slice(-1)[0] || '';
        messages.push(field ? `${field}: ${e.msg}` : e.msg);
      }
    } else {
      messages.push(error.detail || '请求参数错误');
    }
    const msg = messages.join('; ') || '请求参数错误';
    config.onError?.(`接口调用失败: ${msg}`, 'error');
    throw new Error(msg);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: '请求失败' }));
    throw new Error(error.detail || `请求失败: ${response.status}`);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

export const get = <T>(endpoint: string, timeoutMs?: number) => request<T>('GET', endpoint, undefined, timeoutMs);
export const post = <T>(endpoint: string, body?: unknown, timeoutMs?: number) => request<T>('POST', endpoint, body, timeoutMs);
export const put = <T>(endpoint: string, body?: unknown, timeoutMs?: number) => request<T>('PUT', endpoint, body, timeoutMs);
export const patch = <T>(endpoint: string, body?: unknown, timeoutMs?: number) => request<T>('PATCH', endpoint, body, timeoutMs);
export const del = <T>(endpoint: string, timeoutMs?: number) => request<T>('DELETE', endpoint, undefined, timeoutMs);

/**
 * Download a blob/file from the API (for attachments, exports, etc.)
 */
export async function downloadBlob(
  endpoint: string,
  timeoutMs: number = 60000,
  retryAuth = true,
): Promise<Blob> {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(url, { headers, signal: controller.signal, credentials: 'include' });
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'AbortError') {
      config.onError?.(`下载超时（${timeoutMs / 1000}s）`, 'error');
      throw new Error(`下载超时（${timeoutMs / 1000}s）`);
    }
    config.onError?.('下载失败，请检查网络连接', 'error');
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (response.status === 401) {
    if (retryAuth && await refreshSession()) {
      return downloadBlob(endpoint, timeoutMs, false);
    }
    expireSession();
    throw new Error('认证失败');
  }

  if (!response.ok) {
    throw new Error(`下载失败: ${response.status}`);
  }

  return response.blob();
}

/** 使用 XMLHttpRequest 上传文件，支持进度、超时、Cookie 认证和一次刷新重试。 */
function uploadFileAttempt<T>(
  url: string,
  formData: FormData,
  onProgress: ((pct: number) => void) | undefined,
  retryAuth: boolean,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
    }
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText));
      } else if (xhr.status === 401) {
        if (!retryAuth) {
          expireSession();
          reject(new Error('认证失败'));
          return;
        }
        void refreshSession()
          .then((refreshed) => {
            if (!refreshed) {
              expireSession();
              throw new Error('认证失败');
            }
            return uploadFileAttempt<T>(url, formData, onProgress, false);
          })
          .then(resolve, reject);
      } else {
        try {
          const err = JSON.parse(xhr.responseText);
          reject(new Error(err.detail || '上传失败'));
        } catch {
          reject(new Error(`上传失败 (${xhr.status})`));
        }
      }
    });
    xhr.addEventListener('error', () => reject(new Error('网络错误')));
    xhr.addEventListener('timeout', () => reject(new Error('上传超时（60s）')));
    xhr.open('POST', `${API_BASE_URL}${url}`);
    xhr.withCredentials = true;
    xhr.timeout = 60000;
    if (accessToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    }
    xhr.send(formData);
  });
}

/** 上传文件并报告进度。 */
export function uploadFile<T>(url: string, formData: FormData, onProgress?: (pct: number) => void): Promise<T> {
  return uploadFileAttempt<T>(url, formData, onProgress, true);
}
